/**
 * Générateur — Automatisme : calculer le pourcentage d'une quantité.
 * Attendu ID : 1252 | Niveau : 3ème | Domaine : Proportionnalité
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 19, ligne 1389).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTENSION de gen_1056 (5e : 1 %, 10 %, 50 %) aux pourcentages COMPOSÉS :
 * 5, 15, 20, 25, 30, 40, 75 % (deux fois sur trois), 1, 10, 50 % en
 * entretien. Mêmes écritures et mêmes formats que 1056 (les solveurs sont
 * les siens) ; le domaine est resserré aux résultats ENTIERS : N multiple
 * de 20 pour les composés (5 % de 20 = 1), multiple de 10 ou 100 pour
 * 10 % et 1 %, pair pour 50 % ; N ≤ 1 000. Lu « prendre p % de N ». Hors :
 * 33 % (pas exact), 100 %, « quel pourcentage ? » (5e l. 1328, sans gen),
 * 15 et 40 % admis (choix mode auto, À ARBITRER).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NC9 /
 * 5NC10 (p = 5, 10, 20, 30, 40, 50, 25, 75, 33, 100, 1 ; N = b × 12 ou
 * 2..50), 4DC3 (N = b × 20).
 *
 * ── Plan de cartes (ceux de 1056) ─────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre égalités
 *     « 25 % de 240 = ⬚ », deux par ligne, pourcentages distincts.
 *   Carte 2 « Prendre un pourcentage » (pct_qcm_unique) : « Combien vaut
 *     25 % de 240 € ? », QUATRE choix ; (pct_tableau_vf) : trois lignes
 *     « 25 % de 240, c'est 60. ».
 *
 * INVARIANTS : valeurs en CENTIÈMES entiers (exactitude), résultat entier
 * par construction (assert) ; distracteurs distincts de la clé et entre eux.
 * SACRIFIÉS (fiche trois pages : paquets de 10 %, moitié de la moitié,
 * moitié de 10 %) : N = 240 pour tous les pourcentages ; 300 importé de 1056
 * ; auto-testés par gen1252.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP 4e 2023 q.16 : la recopie de 10 %
 * ≈ 40 %) ─────────────────────────────────────────────────────────────────
 *   pourcentage_recopie     : p pris pour la réponse (PRINCIPALE) ;
 *   mauvais_diviseur        : N ÷ p (25 % → N ÷ 25) ;
 *   composition_fausse      : 75 % → 70 % (les dizaines seules), NEUVE ;
 *   composition_incomplete  : 30 % → 10 % (une seule dizaine), NEUVE ;
 *   moitie_seule            : 25 % → 50 % (la moitié, pas la moitié de la moitié) ;
 *   reste                   : N − la clé (le piège naturel de 25 / 75) ;
 *   sens_inverse            : N × 100 ÷ p.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { SACRIFIE as SACRIFIE_1056 } from '../../5eme/nombres-rationnels/gen_1056';

const ATTENDU_ID = 1252;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_PCT = 'Prendre un pourcentage';

// ─── Le domaine ───────────────────────────────────────────────────────────

const COMPOSES = [5, 15, 20, 25, 30, 40, 75];
const SIMPLES = [1, 10, 50];
const UNITES = ['€', 'g', 'km', 'm', 'L'];
/** Le nombre de la fiche (240, tous pourcentages) et celui de 1056 (300). */
export const SACRIFIES = [240, SACRIFIE_1056];

/** Centièmes → écriture française (350 → « 3,5 », 4500 → « 45 »). */
const fmt = (c) => { const e = Math.floor(c / 100); const r = c % 100; return r === 0 ? String(e) : `${e},${String(r).padStart(2, '0').replace(/0$/, '')}`; };
/** « a % de N », espace insécable avant % (la même écriture que 1056 : ses solveurs la relisent). */
const pctDe = (it) => `${it.pct} % de ${it.N}${it.unite ? ` ${it.unite}` : ''}`;
const avecUnite = (c, it) => `${fmt(c)}${it.unite ? ` ${it.unite}` : ''}`;

/** Un item { pct, N, val (centièmes), unite } ; assert : résultat entier, deux chemins. */
const item = (pct, N, unite = null) => {
    if (SACRIFIES.includes(N) || N < 10 || N > 1000) return null;
    const val = N * pct; // en centièmes
    if (val % 100 !== 0 || val !== (N / 100) * pct * 100) return null;
    return { pct, N, val, unite };
};

/** Un item d'un pourcentage : N multiple de 20 (composés), de 10 (10 %), de 100 (1 %), pair (50 %). */
const tirerItem = (pct, { unite = null, exclusN = [] } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        let N;
        if (pct === 1) N = 100 * randInt(1, 10);
        else if (pct === 10) N = 10 * randInt(2, 100);
        else if (pct === 50) N = 2 * randInt(6, 500);
        // composés : des nombres qui se composent de tête (≤ 400 ; multiples de 40 pour 25 et 75 %)
        else if (pct === 25 || pct === 75) N = 40 * randInt(1, 10);
        else if (pct === 15) N = 20 * randInt(1, 15);
        else N = 20 * randInt(1, 20);
        if (exclusN.includes(N)) continue;
        const it = item(pct, N, unite);
        if (it) return it;
    }
    return null;
};
const tirerPct = () => (randInt(1, 3) === 1 ? randomChoice(SIMPLES) : randomChoice(COMPOSES));

const EXPLICATIONS = {
    pourcentage_recopie: '25 %, ce n\'est pas 25 : c\'est 25 pour 100. 25 % de 240, c\'est le quart de 240 : 60.',
    mauvais_diviseur: '25 %, c\'est un quart (÷ 4), pas ÷ 25 ; 20 %, c\'est ÷ 5 ; 10 %, ÷ 10.',
    composition_fausse: '75 %, c\'est 50 % + 25 % (ou 7 dizaines et une demi-dizaine) : 75 % de 240 = 120 + 60 = 180.',
    composition_incomplete: '30 %, c\'est trois fois 10 % : 30 % de 240 = 3 × 24 = 72.',
    moitie_seule: '25 %, c\'est la moitié de la moitié : 240 → 120 → 60.',
    reste: 'On demande la partie prise, pas ce qui reste.',
    sens_inverse: 'Prendre un pourcentage plus petit que 100 %, c\'est prendre une partie : le résultat est plus petit que le nombre.',
    regle: 'Je pars de 10 % (÷ 10) ou de 50 % (la moitié) et je compose : 20 % = 2 × 10 %, 5 % = la moitié de 10 %, 25 % = la moitié de 50 %, 75 % = 50 % + 25 %.',
};

// ─── Distracteurs (en centièmes) ──────────────────────────────────────────

const tousCandidats = (it) => {
    const { pct, N, val } = it;
    const c = [];
    const pousser = (code, v) => { if (Number.isInteger(v) && v > 0 && v !== val && v !== N * 100 && !c.some((x) => x.val === v)) c.push({ code, val: v }); };
    pousser('pourcentage_recopie', pct * 100);
    if ((N * 100) % pct === 0) pousser('mauvais_diviseur', (N * 100) / pct);
    if (pct >= 10 && pct % 10 !== 0) pousser('composition_fausse', N * (pct - (pct % 10)));
    if (pct % 10 === 0 && pct > 10 && pct !== 50) pousser('composition_incomplete', N * 10);
    if (pct === 25 || pct === 75) pousser('moitie_seule', N * 50);
    if (pct === 5) pousser('composition_fausse', N * 10);
    if (pct === 15) pousser('composition_fausse', N * 10);
    pousser('reste', N * 100 - val);
    if (pct < 50) pousser('sens_inverse', (N * 100 * 100) / pct);
    return c.filter((x) => x.val % 100 === 0 || pct === 1);
};
const candidats = (it) => {
    const parCode = new Map();
    for (const x of shuffle(tousCandidats(it))) if (!parCode.has(x.code)) parCode.set(x.code, x);
    return [...parCode.values()];
};
/** Trois familles distinctes, pourcentage_recopie en premier deux fois sur trois. */
const distracteursQcm = (it) => {
    const cands = candidats(it);
    const recopie = cands.find((x) => x.code === 'pourcentage_recopie');
    const autres = shuffle(cands.filter((x) => x.code !== 'pourcentage_recopie'));
    const pris = recopie && randInt(1, 3) !== 1 ? [recopie, ...autres] : shuffle(cands);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const ligneFlash = (it) => `${pctDe(it)} = {{${fmt(it.val)}}}`;
const phraseFlash = (items) => [items.slice(0, 2), items.slice(2, 4)].map((paire) => paire.map(ligneFlash).join(' ; ')).join('\n');

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        const pcts = shuffle([...COMPOSES]).slice(0, 3).concat([randomChoice(SIMPLES)]);
        const items = [];
        for (const pct of shuffle(pcts)) { const it = tirerItem(pct, { exclusN: items.map((x) => x.N) }); if (!it) break; items.push(it); }
        if (items.length < 4) continue;
        const payload = makeTexteTrou(phraseFlash(items), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: false });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, pourcentage_recopie: EXPLICATIONS.pourcentage_recopie } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [item(25, 200), item(30, 140), item(5, 60), item(10, 350)];
    const payload = makeTexteTrou(phraseFlash(items), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2 : QCM et Vrai/Faux ───────────────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const unite = randomChoice([null, randomChoice(UNITES)]);
        const it = tirerItem(tirerPct(), { unite });
        if (!it) continue;
        const faux = distracteursQcm(it);
        if (!faux) continue;
        const bon = avecUnite(it.val, it);
        const choix = [bon, ...faux.map((f) => avecUnite(f.val, it))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>Combien vaut ${pctDe(it)} ?</b></p>`, opts(L_PCT));
        payload.contenu = { ...payload.contenu, sousType: String(it.pct), explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 25 % de 200 = 50 ; 25 (recopie), 8 (÷ 25), 150 (reste).
    const it = item(25, 200);
    const payload = makeQcm(['50', '25', '8', '150'], '50', `<p><b>Combien vaut ${pctDe(it)} ?</b></p>`, opts(L_PCT));
    payload.contenu = { ...payload.contenu, sousType: '25', explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'pourcentage_recopie' }, { code: 'mauvais_diviseur' }, { code: 'reste' }] };
    return payload;
};

const affirmation = (it, vraie) => {
    if (vraie) return { ligne: `${pctDe(it)}, c'est ${avecUnite(it.val, it)}.`, code: null };
    const cands = candidats(it);
    if (!cands.length) return null;
    const d = randomChoice(cands);
    return { ligne: `${pctDe(it)}, c'est ${avecUnite(d.val, it)}.`, code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const unite = randomChoice([null, randomChoice(UNITES)]);
        const pcts = shuffle([...COMPOSES]).slice(0, 2).concat([randomChoice(SIMPLES)]);
        const items = [];
        for (const pct of shuffle(pcts)) { const it = tirerItem(pct, { unite, exclusN: items.map((x) => x.N) }); if (!it) break; items.push(it); }
        if (items.length < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const affs = items.map((it, i) => affirmation(it, verites[i]));
        if (affs.some((a) => !a)) continue;
        const codes = affs.filter((a) => a.code).map((a) => a.code);
        const nbInv = codes.filter((c) => c === 'sens_inverse').length;
        if (nbInv > 1 || (nbInv === 1 && codes.length === 1)) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_PCT));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    const items = [item(25, 200), item(30, 140), item(10, 350)];
    const lignes = [`${pctDe(items[0])}, c'est 50.`, `${pctDe(items[1])}, c'est 14.`, `${pctDe(items[2])}, c'est 35.`];
    const payload = makeVraiFaux(lignes, [0, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_PCT));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items, verites: [true, false, true], distracteurs: [{ code: 'composition_incomplete' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'valeurs en centièmes entiers, résultat entier par construction ; quatre choix distincts',
        'pourcentages composés 5, 15, 20, 25, 30, 40, 75 (deux fois sur trois) et 1, 10, 50 en entretien ; 33 et 100 exclus',
        'sacrifiés : 240 (tous pourcentages) et 300 (1056)',
    ],
    pct_qcm_unique: {
        familles: {
            pourcentage_recopie: 'p (la principale, DEPP 4e 2023 q.16), en premier deux fois sur trois',
            mauvais_diviseur: 'N ÷ p',
            composition_fausse: 'les dizaines seules (75 % → 70 %, 15 % → 10 %, 5 % → 10 %)',
            composition_incomplete: 'une seule dizaine (30 % → 10 %)',
            moitie_seule: '50 % pour 25 ou 75 %',
            reste: 'N − la clé',
            sens_inverse: 'N × 100 ÷ p (jamais seule fausse au Vrai/Faux)',
        },
    },
    vf_tableau_vf: 'trois lignes, deux composés et un simple, une ou deux vraies',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    pct_qcm_unique: genQcm,
    pct_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'pct', label: L_PCT, formatsList: [
        { key: 'pct_qcm_unique', label: 'QCM' },
        { key: 'pct_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
